import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Check } from 'lucide-react';
import { locationData } from '../AiDiet/BasicInformation';
import { submitCosmeticInfo } from './CosmeticSkinCareApi';

import basicInfoIcon from '../../Assets/Images/rewamp/skin/1. Basic details/basic info.svg';
import sleepIcon from '../../Assets/Images/rewamp/skin/3. Results/section 3/external stressor/sleep quality.svg';
import locationIcon from '../../Assets/Images/rewamp/skin/1. Basic details/location in map.svg';
import acneProneIcon from '../../Assets/Images/rewamp/skin/1. Basic details/is your skin prone to acne.svg';
import skinPreferenceIcon from '../../Assets/Images/rewamp/skin/1. Basic details/skin preference and concern.svg';
import improveIcon from '../../Assets/Images/rewamp/skin/1. Basic details/what would you like to improve.svg';
import sunExposureIcon from '../../Assets/Images/rewamp/skin/1. Basic details/exposed to sun.svg';

const STEPPER = ['Face Scan', 'Details', 'Analysis', 'Results'];

const GENDER_OPTIONS = [
  { label: 'Male', value: 'MALE' },
  { label: 'Female', value: 'FEMALE' },
  { label: 'Others', value: 'OTHERS' },
  { label: 'Prefer not to say', value: 'PREFER_NOT_TO_SAY' },
];

const CLIMATE_OPTIONS = [
  { label: 'Tropical', value: 'TROPICAL' },
  { label: 'Dry', value: 'DRY' },
  { label: 'Temperate', value: 'TEMPERATE' },
  { label: 'Cold', value: 'COLD' },
  { label: 'Humid', value: 'HUMID' },
];

const SKIN_TYPE_OPTIONS = [
  { label: 'Oily', value: 'OILY' },
  { label: 'Dry', value: 'DRY' },
  { label: 'Combination', value: 'COMBINATION' },
  { label: 'Normal', value: 'NORMAL' },
  { label: 'Sensitive', value: 'SENSITIVE' },
];

// Sun Sensitivity Assessment is hidden from the UI, but the backend still
// requires fitzpatrick_answers on submit — send a neutral mid-scale default.
const DEFAULT_FITZPATRICK_ANSWERS = { skin_color_score: 2, sun_reaction_score: 2, eye_color_score: 2 };

const CONCERN_GROUPS = [
  { category: 'Acne & Blemishes', options: [
    { label: 'Acne', value: 'ACNE' },
    { label: 'Acne scars', value: 'ACNE_SCARS' },
    { label: 'Blackheads', value: 'BLACKHEADS' },
    { label: 'Whiteheads', value: 'WHITEHEADS' },
  ] },
  { category: 'Pigmentation & Tone', options: [
    { label: 'Dark spots', value: 'DARK_SPOTS' },
    { label: 'Pigmentation', value: 'PIGMENTATION' },
    { label: 'Dull skin', value: 'DULL_SKIN' },
    { label: 'Uneven skin tone', value: 'UNEVEN_SKIN_TONE' },
  ] },
  { category: 'Aging', options: [
    { label: 'Wrinkles', value: 'WRINKLES' },
    { label: 'Fine lines', value: 'FINE_LINES' },
  ] },
  { category: 'Texture & Oil', options: [
    { label: 'Dryness', value: 'DRYNESS' },
    { label: 'Oiliness', value: 'OILINESS' },
    { label: 'Large pores', value: 'LARGE_PORES' },
  ] },
  { category: 'Redness', options: [
    { label: 'Redness', value: 'REDNESS' },
    { label: 'Rosacea', value: 'ROSACEA' },
  ] },
  { category: 'Eye Area', options: [
    { label: 'Dark circles', value: 'DARK_CIRCLES' },
    { label: 'Puffy eyes', value: 'PUFFY_EYES' },
  ] },
];

const SENSITIVITY_OPTIONS = [
  { label: 'Never', value: 'NEVER' },
  { label: 'Sometimes', value: 'SOMETIMES' },
  { label: 'Often', value: 'OFTEN' },
  { label: 'Always', value: 'ALWAYS' },
];

const MAKEUP_OPTIONS = [
  { label: 'Never', value: 'NEVER' },
  { label: 'Occasionally', value: 'OCCASIONALLY' },
  { label: 'Daily', value: 'DAILY' },
];

const SUNSCREEN_OPTIONS = [
  { label: 'Never', value: 'NEVER' },
  { label: 'Only outdoors', value: 'ONLY_OUTDOORS' },
  { label: 'Daily', value: 'DAILY' },
  { label: 'Reapply every 2-3 hours', value: 'REAPPLY_EVERY_2_3_HOURS' },
];

const SUN_EXPOSURE_OPTIONS = [
  { label: '<15 min', value: 'LESS_THAN_15_MIN' },
  { label: '15-30 min', value: 'MIN_15_30' },
  { label: '30-60 min', value: 'MIN_30_60' },
  { label: '1 hr+', value: 'HOUR_PLUS' },
];

const SLEEP_OPTIONS = [
  { label: '<5 hrs', value: 'LESS_THAN_5' },
  { label: '5-6 hrs', value: 'HRS_5_6' },
  { label: '6-7 hrs', value: 'HRS_6_7' },
  { label: '7-8 hrs', value: 'HRS_7_8' },
  { label: '8 hrs+', value: 'HRS_8_PLUS' },
];

const WATER_INTAKE_OPTIONS = [
  { label: '<1L', value: 'LESS_THAN_1L' },
  { label: '1-2L', value: 'L_1_2' },
  { label: '2-3L', value: 'L_2_3' },
  { label: '3L+', value: 'L_3_PLUS' },
];

const STRESS_OPTIONS = [
  { label: 'Low', value: 'LOW' },
  { label: 'Moderate', value: 'MODERATE' },
  { label: 'High', value: 'HIGH' },
];

const POLLUTION_OPTIONS = [
  { label: 'Low', value: 'LOW' },
  { label: 'Medium', value: 'MEDIUM' },
  { label: 'High', value: 'HIGH' },
];

const calculateAge = (dob) => {
  if (!dob) return '';
  const birthDate = new Date(dob);
  if (Number.isNaN(birthDate.getTime())) return '';
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

const mapGender = (g) => {
  if (!g) return '';
  const upper = String(g).toUpperCase();
  if (upper === 'M' || upper === 'MALE') return 'MALE';
  if (upper === 'F' || upper === 'FEMALE') return 'FEMALE';
  return upper;
};

const PillGroup = ({ options, selected, multi = false, onToggle, disabled }) => (
  <div className="cd-pill-row">
    {options.map((opt) => {
      const isSelected = multi ? selected.includes(opt.value) : selected === opt.value;
      const isDisabled = disabled && !isSelected;
      return (
        <button
          type="button"
          key={opt.value}
          className={`cd-pill${isSelected ? ' selected' : ''}`}
          disabled={isDisabled}
          onClick={() => onToggle(opt.value)}
        >
          {opt.label}
        </button>
      );
    })}
  </div>
);

const RadioRow = ({ name, options, value, onChange }) => (
  <div className="cd-radio-row">
    {options.map((opt) => (
      <label className="cd-radio-label" key={opt.value}>
        <input
          type="radio"
          name={name}
          checked={value === opt.value}
          onChange={() => onChange(opt.value)}
          style={{ accentColor: '#004276' }}
        />
        {opt.label}
      </label>
    ))}
  </div>
);

const RequiredMark = () => <span className="cd-required-mark">*</span>;

const FieldError = ({ show, children }) => (show ? <div className="cd-field-error">{children}</div> : null);

const SectionHeader = ({ icon, title, subtitle }) => (
  <div className="cd-section-header">
    <img src={icon} alt="" className="cd-section-icon" />
    <div>
      <div className="cd-section-title">{title}</div>
      <div className="cd-section-subtitle">{subtitle}</div>
    </div>
  </div>
);

const Accordion = ({ icon, title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="cd-accordion">
      <button type="button" className="cd-accordion-header" onClick={() => setOpen((o) => !o)}>
        <span className="cd-accordion-header-left">
          <img src={icon} alt="" width={20} height={20} />
          {title}
        </span>
        {open ? <ChevronUp size={18} color="#8B86A8" /> : <ChevronDown size={18} color="#8B86A8" />}
      </button>
      {open && <div className="cd-accordion-body">{children}</div>}
    </div>
  );
};

const CosmeticDetails = () => {
  const navigate = useNavigate();
  const loginObj = JSON.parse(localStorage.getItem('loginObj') || 'null');

  const [subStep, setSubStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const [form, setForm] = useState({
    age: calculateAge(loginObj?.dob) || '',
    sex: mapGender(loginObj?.gender),
    country: 'India',
    state: 'Odisha',
    region: 'Bhubaneswar',
    climate: '',
    skinType: '',
    concerns: [],
    sensitivity: '',
    makeupUsage: '',
    sunscreenUsage: '',
    sunExposure: '',
    sleep: '',
    waterIntake: '',
    stressLevel: '',
    pollutionExposure: '',
  });

  const [attemptedNext, setAttemptedNext] = useState(false);
  const fieldRefs = useRef({});

  // loginObj rarely carries dob/gender reliably — pull authoritative age/gender
  // from the same patient-info endpoint the AI Diet form uses, and only fill in
  // fields the user hasn't already touched.
  useEffect(() => {
    const patientCode = loginObj?.code;
    if (!patientCode) return undefined;
    let active = true;
    (async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_SMARTDIET_BASEURL}user/patientinfo?patient_id=${patientCode}`
        );
        const data = Array.isArray(res.data) ? res.data[0] : res.data?.data?.[0];
        if (!active || !data) return;
        setForm((prev) => ({
          ...prev,
          age: prev.age || (data.AGE ?? ''),
          sex: prev.sex || (data.GENDER === 'M' ? 'MALE' : data.GENDER === 'F' ? 'FEMALE' : prev.sex),
        }));
      } catch (err) {
        console.error('Failed to autofill age/gender from patient info:', err);
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const availableStates = Object.keys(locationData[form.country] || {});
  const availableCities = locationData[form.country]?.[form.state] || [];

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const toggleConcern = (value) =>
    setForm((prev) => {
      if (prev.concerns.includes(value)) {
        return { ...prev, concerns: prev.concerns.filter((v) => v !== value) };
      }
      return { ...prev, concerns: [...prev.concerns, value] };
    });

  const handleStateChange = (e) => {
    const state = e.target.value;
    const cities = locationData[form.country]?.[state] || [];
    setForm((prev) => ({ ...prev, state, region: cities[0] || '' }));
  };

  const handleBack = () => {
    if (subStep === 2) {
      setSubStep(1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    navigate('/cosmetic-skincare-analysis/face-scan');
  };

  // Single source of truth for Basic Information's required fields — used to
  // gate "Next" and to drive the inline per-field error hints below.
  const isMissing = (key) => {
    if (key === 'age') return !form.age;
    if (key === 'sex') return !form.sex;
    if (key === 'skinType') return !form.skinType;
    if (key === 'climate') return !form.climate;
    if (key === 'concerns') return form.concerns.length === 0;
    return false;
  };
  const REQUIRED_FIELDS = ['age', 'sex', 'skinType', 'climate', 'concerns'];

  const handleNext = () => {
    const firstMissing = REQUIRED_FIELDS.find(isMissing);
    if (firstMissing) {
      setAttemptedNext(true);
      const el = fieldRefs.current[firstMissing];
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setAttemptedNext(false);
    setSubStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDone = async () => {
    if (subStep !== 2 || submitting) return;
    setSubmitError('');

    // Safety net — Basic Information is already validated before subStep can
    // reach 2 via handleNext, so this should never actually trigger.
    if (REQUIRED_FIELDS.some(isMissing)) {
      setAttemptedNext(true);
      setSubStep(1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSubmitting(true);
    try {
      const requestId = sessionStorage.getItem('cosmeticRequestId');
      const payload = {
        requestId,
        patientId: loginObj?.code,
        age: Number(form.age) || 0,
        sex: form.sex,
        location: {
          country: form.country,
          state: form.state,
          region: form.region,
          latitude: 0,
          longitude: 0,
        },
        climate: form.climate,
        skin_type: form.skinType,
        primary_concerns: form.concerns,
        fitzpatrick_answers: DEFAULT_FITZPATRICK_ANSWERS,
        sourceChannel: loginObj?.sourcechannel,
        createdBy: loginObj?.createdBy,
      };

      // Section B is optional — only include fields the patient actually answered,
      // since sending an empty string fails the backend's enum validation.
      if (form.sensitivity) payload.sensitivity = form.sensitivity;
      if (form.makeupUsage) payload.makeup_usage = form.makeupUsage;
      if (form.sunscreenUsage) payload.sunscreen_usage = form.sunscreenUsage;
      if (form.sunExposure) payload.sun_exposure = form.sunExposure;
      if (form.sleep) payload.sleep = form.sleep;
      if (form.waterIntake) payload.water_intake = form.waterIntake;
      if (form.stressLevel) payload.stress_level = form.stressLevel;
      if (form.pollutionExposure) payload.pollution_exposure = form.pollutionExposure;

      await submitCosmeticInfo(payload);
      navigate('/cosmetic-skincare-analysis/analysis');
    } catch (err) {
      setSubmitError('Something went wrong submitting your details. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto w-full">
      <style>{`
        .cd-wrapper {
          background-color: #fff; background-image: var(--section-bg-gradient);
          padding: 24px 40px 48px 40px;
        }

        .cd-required-mark {
          color: #FF6B6B;
          margin-left: 2px;
        }

        .cd-field-error {
          font-size: 11px;
          color: #D14343;
          margin-top: 6px;
        }

        .cd-header {
          display: grid;
          grid-template-columns: 1fr auto 1fr;
          align-items: center;
          margin-bottom: 24px;
        }

        .cd-back {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 600;
          font-size: 16px;
          color: #1A1A2E;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
          justify-self: start;
        }

        .cd-stepper {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
        }

        .cd-stepper-caption {
          font-size: 12px;
          color: #676C80;
        }

        .cd-stepper-track {
          display: flex;
          align-items: center;
        }

        .cd-stepper-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          width: 90px;
        }

        .cd-stepper-circle {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: 700;
          background: #ffffff;
          border: 1.5px solid #C9C4D9;
          color: #8B86A8;
        }

        .cd-stepper-item.active .cd-stepper-circle {
          background: var(--color-brand-primary);
          border-color: var(--color-brand-primary);
          color: #ffffff;
        }

        .cd-stepper-item.done .cd-stepper-circle {
          background: #ffffff;
          border-color: var(--color-brand-primary);
          color: var(--color-brand-primary);
        }

        .cd-stepper-label {
          font-size: 12px;
          font-weight: 600;
          color: #8B86A8;
        }

        .cd-stepper-item.active .cd-stepper-label,
        .cd-stepper-item.done .cd-stepper-label {
          color: var(--color-brand-primary);
        }

        .cd-stepper-line {
          width: 60px;
          height: 1.5px;
          background: #C9C4D9;
          margin-bottom: 20px;
        }

        .cd-stepper-line.done {
          background: var(--color-brand-primary);
        }

        .cd-content {
          width: 100%;
        }

        .cd-heading {
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          font-size: 26px;
          line-height: 32px;
          color: var(--color-brand-primary);
          margin-bottom: 6px;
        }

        .cd-heading-desc {
          font-size: 14px;
          color: var(--color-brand-primary);
          margin-bottom: 24px;
        }

        .cd-heading-row {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 16px;
        }

        .cd-heading-row .cd-heading {
          margin-bottom: 6px;
        }

        .cd-skip-btn {
          flex-shrink: 0;
          background: #FFFFFF;
          border: 1.5px solid var(--color-brand-primary);
          color: var(--color-brand-primary);
          font-size: 14px;
          font-weight: 600;
          padding: 8px 18px;
          border-radius: 8px;
          cursor: pointer;
          white-space: nowrap;
        }

        .cd-skip-btn:hover:not(:disabled) {
          background: #EAF3FA;
        }

        .cd-skip-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .cd-section-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
        }

        .cd-section-icon {
          width: 48px;
          height: 48px;
          flex-shrink: 0;
        }

        .cd-section-title {
          font-weight: 700;
          font-size: 16px;
          color: var(--color-brand-primary);
        }

        .cd-section-subtitle {
          font-size: 12px;
          color: #676C80;
        }

        .cd-card {
          background: #ffffff;
          border: 1px solid #DFF1FB;
          border-radius: 14px;
          padding: 18px 20px;
          margin-bottom: 16px;
          box-shadow: 0px 2px 8px 0px #00000010;
        }

        .cd-basic-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
        }

        .cd-field-label {
          font-weight: 600;
          font-size: 13px;
          color: #1A1A2E;
          margin-bottom: 10px;
        }

        .cd-field-hint {
          font-size: 11px;
          color: #9490A8;
          margin-top: 8px;
        }

        .cd-age-input {
          width: 100px;
          padding: 8px 10px;
          border: 1px solid #D9D6E3;
          border-radius: 8px;
          font-size: 14px;
          color: #1A1A2E;
        }

        .cd-pill-row {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .cd-pill {
          border-radius: 999px;
          border: 1px solid #D9D6E3;
          background: #ffffff;
          color: #676C80;
          font-size: 13px;
          font-weight: 500;
          padding: 7px 14px;
          cursor: pointer;
        }

        .cd-pill:hover:not(:disabled) {
          border-color: #80DAF1;
        }

        .cd-pill:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .cd-pill.selected {
          background: var(--color-brand-primary);
          color: #ffffff;
          border-color: transparent;
        }

        .cd-radio-row {
          display: flex;
          flex-wrap: wrap;
          gap: 24px;
        }

        .cd-radio-label {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          color: #1A1A2E;
          cursor: pointer;
        }

        .cd-accordion {
          background: #ffffff;
          border: 1px solid #DFF1FB;
          border-radius: 14px;
          margin-bottom: 16px;
          overflow: hidden;
        }

        .cd-accordion-header {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: none;
          border: none;
          padding: 16px 20px;
          cursor: pointer;
          font-family: inherit;
        }

        .cd-accordion-header-left {
          display: flex;
          align-items: center;
          gap: 10px;
          font-weight: 700;
          font-size: 14px;
          color: var(--color-brand-primary);
        }

        .cd-accordion-body {
          padding: 0 20px 20px 20px;
        }

        .cd-select-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
        }

        .cd-select-label {
          font-size: 11px;
          font-weight: 600;
          color: #676C80;
          margin-bottom: 6px;
        }

        .cd-select {
          width: 100%;
          padding: 8px 10px;
          border: 1px solid #D9D6E3;
          border-radius: 8px;
          font-size: 13px;
          color: #1A1A2E;
          background: #ffffff;
        }

        .cd-concern-group {
          margin-bottom: 16px;
        }

        .cd-concern-group:last-child {
          margin-bottom: 0;
        }

        .cd-concern-category {
          font-size: 12px;
          font-weight: 700;
          color: #676C80;
          margin-bottom: 8px;
        }

        .cd-nav-row {
          display: flex;
          justify-content: space-between;
          margin: 20px 0;
        }

        .cd-btn-outline {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 600;
          font-size: 14px;
          background: #ffffff;
          border: 1.5px solid #D9D6E3;
          color: #676C80;
          border-radius: 999px;
          padding: 10px 22px;
          cursor: pointer;
        }

        .cd-btn-primary {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 600;
          font-size: 14px;
          background: var(--color-brand-primary);
          color: #ffffff;
          border: none;
          border-radius: 999px;
          padding: 10px 24px;
          cursor: pointer;
        }

        .cd-btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .cd-form-error {
          text-align: center;
          font-size: 12px;
          color: #D14343;
          margin: 12px 0 -8px 0;
        }

        @media (max-width: 900px) {
          .cd-basic-grid { grid-template-columns: 1fr; }
          .cd-select-grid { grid-template-columns: repeat(2, 1fr); }
        }

        @media (max-width: 768px) {
          .cd-wrapper { padding: 16px 56px 28px 16px; }
          .cd-header { grid-template-columns: 1fr; gap: 12px; }
          .cd-stepper-track { flex-wrap: nowrap; overflow-x: auto; justify-content: flex-start; -webkit-overflow-scrolling: touch; }
          .cd-stepper-item { width: 62px; flex-shrink: 0; }
          .cd-stepper-line { width: 20px; flex-shrink: 0; }
          .cd-heading { font-size: 21px; line-height: 27px; }
          .cd-radio-row { gap: 14px; }
          .cd-card, .cd-accordion-body { padding: 14px 16px; }
        }

        @media (max-width: 480px) {
          .cd-wrapper { padding: 12px 52px 24px 12px; }
          .cd-select-grid { grid-template-columns: 1fr; }
          .cd-nav-row { gap: 10px; }
          .cd-btn-outline, .cd-btn-primary { flex: 1; justify-content: center; padding: 10px 14px; }
        }
      `}</style>

      <div className="cd-wrapper">
        <div className="cd-header">
          <button className="cd-back" onClick={handleBack}>
            <ChevronLeft size={20} />
            Details
          </button>

          <div className="cd-stepper">
            <span className="cd-stepper-caption">Step 2 of 4</span>
            <div className="cd-stepper-track">
              {STEPPER.map((label, i) => (
                <React.Fragment key={label}>
                  {i > 0 && <span className={`cd-stepper-line${i <= 1 ? ' done' : ''}`} />}
                  <div className={`cd-stepper-item${i === 1 ? ' active' : ''}${i === 0 ? ' done' : ''}`}>
                    <span className="cd-stepper-circle">{i === 0 ? <Check size={13} /> : i + 1}</span>
                    <span className="cd-stepper-label">{label}</span>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>

          <span />
        </div>

        <div className="cd-content">
          {subStep === 1 ? (
            <>
              <h1 className="cd-heading">Let's get to know your skin better</h1>
              <p className="cd-heading-desc">
                Help us refine your analysis with a few details for more accurate and personalized skincare insights.
              </p>

              <SectionHeader icon={basicInfoIcon} title="Basic Information" subtitle="Tell us a little about yourself" />

              <div className="cd-card">
                <div className="cd-basic-grid">
                  <div ref={(el) => (fieldRefs.current.age = el)}>
                    <div className="cd-field-label">Age<RequiredMark /></div>
                    <input
                      type="number"
                      min="0"
                      max="120"
                      className="cd-age-input"
                      value={form.age}
                      onChange={(e) => setField('age', e.target.value)}
                    />
                    <div className="cd-field-hint">Auto-filled from your profile — you can adjust it.</div>
                    <FieldError show={attemptedNext && isMissing('age')}>Age is required</FieldError>
                  </div>
                  <div ref={(el) => (fieldRefs.current.sex = el)}>
                    <div className="cd-field-label">Gender<RequiredMark /></div>
                    <PillGroup options={GENDER_OPTIONS} selected={form.sex} onToggle={(v) => setField('sex', v)} />
                    <FieldError show={attemptedNext && isMissing('sex')}>Please select your gender</FieldError>
                  </div>
                  <div ref={(el) => (fieldRefs.current.skinType = el)}>
                    <div className="cd-field-label">Skin Type<RequiredMark /></div>
                    <PillGroup options={SKIN_TYPE_OPTIONS} selected={form.skinType} onToggle={(v) => setField('skinType', v)} />
                    <FieldError show={attemptedNext && isMissing('skinType')}>Please select your skin type</FieldError>
                  </div>
                </div>
              </div>

              <div ref={(el) => (fieldRefs.current.climate = el)}>
                <Accordion icon={locationIcon} title="Location & Climate">
                  <div className="cd-select-grid">
                    <div>
                      <div className="cd-select-label">Country</div>
                      <select className="cd-select" value={form.country} disabled>
                        <option value="India">India</option>
                      </select>
                    </div>
                    <div>
                      <div className="cd-select-label">State</div>
                      <select className="cd-select" value={form.state} onChange={handleStateChange}>
                        {availableStates.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <div className="cd-select-label">Region</div>
                      <select className="cd-select" value={form.region} onChange={(e) => setField('region', e.target.value)}>
                        {availableCities.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="cd-field-label" style={{ marginTop: 16 }}>Climate<RequiredMark /></div>
                  <PillGroup options={CLIMATE_OPTIONS} selected={form.climate} onToggle={(v) => setField('climate', v)} />
                  <div className="cd-field-hint">We use your location and climate to adjust recommendations for environmental conditions.</div>
                  <FieldError show={attemptedNext && isMissing('climate')}>Please select your climate</FieldError>
                </Accordion>
              </div>

              <div ref={(el) => (fieldRefs.current.concerns = el)}>
                <Accordion icon={skinPreferenceIcon} title={<>Primary Skin Concern<RequiredMark /></>}>
                  {CONCERN_GROUPS.map((group) => (
                    <div className="cd-concern-group" key={group.category}>
                      <div className="cd-concern-category">{group.category}</div>
                      <PillGroup
                        options={group.options}
                        selected={form.concerns}
                        multi
                        onToggle={toggleConcern}
                      />
                    </div>
                  ))}
                  <FieldError show={attemptedNext && isMissing('concerns')}>Select at least 1 concern</FieldError>
                </Accordion>
              </div>

              <div className="cd-nav-row">
                <button className="cd-btn-outline" onClick={handleBack}>
                  <ChevronLeft size={16} />
                  Previous
                </button>
                <button className="cd-btn-primary" onClick={handleNext}>
                  Next
                  <ChevronRight size={16} />
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="cd-heading-row">
                <h1 className="cd-heading">Tell us a bit more about your skin</h1>
                <button type="button" className="cd-skip-btn" disabled={submitting} onClick={handleDone}>
                  {submitting ? 'Processing…' : 'Skip for now'}
                </button>
              </div>
              <p className="cd-heading-desc">
                These details are optional but help personalize your skincare routine and recommendations. You can skip and complete this later.
              </p>

              <SectionHeader
                icon={skinPreferenceIcon}
                title="Additional Details"
                subtitle="Help us understand your lifestyle so we can tailor your analysis and recommendations."
              />

              <Accordion icon={acneProneIcon} title="Skin Sensitivity">
                <RadioRow name="sensitivity" options={SENSITIVITY_OPTIONS} value={form.sensitivity} onChange={(v) => setField('sensitivity', v)} />
                <div className="cd-field-hint">This will help us know if your skin will react to any specific product.</div>
              </Accordion>

              <Accordion icon={improveIcon} title="Makeup Usage">
                <RadioRow name="makeupUsage" options={MAKEUP_OPTIONS} value={form.makeupUsage} onChange={(v) => setField('makeupUsage', v)} />
              </Accordion>

              <Accordion icon={sunExposureIcon} title="Sunscreen Usage">
                <RadioRow name="sunscreenUsage" options={SUNSCREEN_OPTIONS} value={form.sunscreenUsage} onChange={(v) => setField('sunscreenUsage', v)} />
              </Accordion>

              <Accordion icon={sunExposureIcon} title="How often are you exposed to sunlight?">
                <RadioRow name="sunExposure" options={SUN_EXPOSURE_OPTIONS} value={form.sunExposure} onChange={(v) => setField('sunExposure', v)} />
                <div className="cd-field-hint">This will help us understand how sunlight may be affecting your skin.</div>
              </Accordion>

              <Accordion icon={sleepIcon} title="Sleep (average)">
                <RadioRow name="sleep" options={SLEEP_OPTIONS} value={form.sleep} onChange={(v) => setField('sleep', v)} />
              </Accordion>

              <Accordion icon={skinPreferenceIcon} title="Water Intake">
                <RadioRow name="waterIntake" options={WATER_INTAKE_OPTIONS} value={form.waterIntake} onChange={(v) => setField('waterIntake', v)} />
              </Accordion>

              <Accordion icon={acneProneIcon} title="Stress Level">
                <RadioRow name="stressLevel" options={STRESS_OPTIONS} value={form.stressLevel} onChange={(v) => setField('stressLevel', v)} />
              </Accordion>

              <Accordion icon={improveIcon} title="Pollution Exposure">
                <RadioRow name="pollutionExposure" options={POLLUTION_OPTIONS} value={form.pollutionExposure} onChange={(v) => setField('pollutionExposure', v)} />
              </Accordion>

              {submitError && <div className="cd-form-error">{submitError}</div>}
              <div className="cd-nav-row">
                <button className="cd-btn-outline" onClick={handleBack}>
                  <ChevronLeft size={16} />
                  Previous
                </button>
                <button className="cd-btn-primary" disabled={submitting} onClick={handleDone}>
                  {submitting ? 'Submitting…' : 'Done'}
                  {!submitting && <Check size={16} />}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CosmeticDetails;
